/**
 * ONE TAP — the procedural sunlit sky and city. (Art direction: SUNLIT CEL, DESIGN.md 4.)
 *
 * THIS IS NOW THE BACKDROP AND THE FILL, NOT A COLOUR BEHIND A WINDOW.
 *
 * The arena used to be an enclosed multi-deck interior, and this shader's entire job was to
 * make the handful of openings in it read as openings — which it did by being brighter than
 * every wall in the building. That constraint is gone with the pivot to open rooftops
 * (DESIGN.md 4, "The Arena Is a ROOFTOP"): the sky is now a large fraction of most frames, it
 * is the thing players look at while they run, and it has to hold up on its own terms.
 *
 * So it is no longer a pale wash. It is:
 *
 *  - A RICH GRADUATED BLUE. Deep azure at the zenith (L* 62, 68% chroma), full palette blue
 *    through the middle of the dome (L* 67-80, 42-62% chroma), a hot pale haze band at the
 *    horizon (L* 87-90, and still 12-20% chroma — a PALE BLUE or a WARM CREAM, never a white).
 *    That is a twenty-seven point vertical gradient where round three had six, and the chroma
 *    climbs the whole way up it. The zenith is allowed to be genuinely saturated because nothing
 *    has to read THROUGH it any more; the value ladder in toon.ts places sunlit white concrete
 *    at L* 95, clear of the sky it stands against, which is the correct exterior relationship
 *    and the exact opposite of the interior one.
 *  - A HORIZON THAT IS WARM WHERE THE SUN IS AND COOL WHERE IT IS NOT. The haze band lerps
 *    between a warm cream and a cool pale blue by azimuth, and it is now 4.9 degrees tall
 *    instead of 9.2. Both halves of that matter and they are separate fixes.
 *
 *    The height was the bigger problem. A first-person camera on a rooftop spends most of its
 *    life pitched at or below the horizon, so the haze band — the palest, least saturated part
 *    of the dome by design — was most of the visible sky in most frames. The deep blue overhead
 *    was real, correct, and almost never on screen. Halving the band and steepening the gradient
 *    under it means the dome is at 75 L* / 47% chroma by twelve degrees of elevation, where it
 *    used to take twenty-five degrees to get there.
 *
 *    The hue is the joy. A cream ring all the way round the horizon reads as smog; a cream ARC
 *    around the sun with cool blue behind you reads as morning, and it costs one lerp.
 *  - SOFT GRAPHIC CUMULUS IN TWO DECKS AT DIFFERENT ALTITUDES, WITH REAL PARALLAX, plus a thin
 *    high cirrus that streaks with the drift. The decks are sampled on virtual planes at 520m
 *    and 2200m and offset by THE CAMERA'S OWN POSITION, so running across a roof slides the near
 *    deck against the far one — about a fifth of a cloud-width over the arena's width. Rotation
 *    alone can never produce that, and it is the difference between a sky painted on the inside
 *    of a box and one that is somewhere else.
 *
 *    Each cloud in the near deck is lit by a SECOND DENSITY TAP TAKEN TOWARD THE SUN'S OWN
 *    PROJECTED POSITION ON THAT PLANE — a one-sample self-shadow with a real light source rather
 *    than a fixed direction. Where the field thins toward the sun, light gets in and that face
 *    goes warm white with a hot silver lining; where it thickens, it goes cool blue. Sixteen L*
 *    of form driven by the shape of the cloud itself, and because the light has a POSITION, a
 *    cloud low in the sky lights on its TOP while one overhead lights on the flank facing the
 *    sun. Lit tops, cool undersides, no two clouds shaded alike — see CLOUD_SUN_STEP.
 *
 *    They also take AERIAL PERSPECTIVE now, the same cue the skyline has always had: a cloud near
 *    the horizon dissolves toward the air in front of it, going cream toward the sun and cool
 *    behind you. Painted, not inked: no halftone, no comic tells (DESIGN.md 4).
 *  - A VISIBLE SUN — clean-edged disc, restrained halo — drawn at the direction lighting.ts
 *    actually lights from, so every shadow on the deck points away from a sun the player can
 *    see and the light shafts in postfx.ts march toward the right pixel.
 *  - A STYLISED CITY SKYLINE in three parallax bands, each hazed further toward the sky than
 *    the one in front of it. AERIAL PERSPECTIVE LIVES HERE AND ONLY HERE: fog over the play
 *    space is what collapsed the value ladder in an earlier pass, so the distance cue is
 *    confined to the shader that draws distance.
 *
 * It is drawn as a single full-screen quad pinned to the far plane with `depthFunc: LEqual`
 * and a high renderOrder, so it only shades the pixels the arena does NOT cover.
 */

import * as THREE from 'three';
import { PALETTE } from '@shared/constants';

/**
 * SKY EXPOSURE, i.e. where the whole dome sits in the value range.
 *
 * DOWN AGAIN, 1.35 -> 1.15, and this one is a measured fix rather than a taste call.
 *
 * A capture of the rooftop build was sampled across a frame that is two-thirds sky. The dome
 * came back at L* 91.7 near the horizon with SIX PERCENT CHROMA — a flat pale card, not a blue
 * sky — and the reason was a collision nobody had checked: toon.ts places a white wall IN SHADE
 * at L* 85.6, and at gain 1.35 the sky passed through 85.6 at nine degrees of elevation. Every
 * shaded parapet, plant-room flank and mast in the arena was silhouetted against a sky of its
 * own value over the whole lower third of the dome. The ink outline was the only thing still
 * describing the architecture, which is the same failure mode the value ladder was built to
 * end, just moved from surface-against-surface to surface-against-sky.
 *
 * Dropping the dome five to six points moves that crossing from nine degrees to THREE — a strip
 * of sky so close to the horizon that the city geometry occupies it anyway — and buys back the
 * saturation at the same time, because the further the sky sits from white the more of its own
 * hue survives. (The other half of that fix is in postfx.ts: the composite's warm-highlight
 * grade used to bleach the sky's blue out on its way to the screen. Sky pixels are now exempt
 * from it. Between the two, the horizon band goes from 6% chroma to 22%.)
 *
 * Computed through the full pipeline (gain -> chroma 1.12 -> exposure 0.85 -> shoulder at 0.72
 * -> black lift -> sRGB, with the sky's grade-tint exemption) in CIE L*, WITH THE RESHAPED DOME
 * (see HAZE_TOP; chroma in brackets, and it is the number that changed most):
 *
 *     hottest horizon, under the sun  91.2 [21%]     horizon away from the sun  82.9 [35%]
 *     sky at  6 deg  79.6 [46%]      sky at 12 deg  76.6 [51%]
 *     sky at 20 deg  71.5 [59%]      sky at 30 deg  66.8 [69%]      zenith 62.1 [76%]
 *     cloud lit 90.2 [9%, WARM]      cloud underside 74.4 [32%, COOL]
 *     far skyline band 83.6   near skyline band 74.3   below-horizon haze 65.5
 *
 * EVERY CHROMA FIGURE IN THAT LIST WENT UP BY FIVE TO EIGHT POINTS WITHOUT A SINGLE COLOUR IN
 * THIS FILE MOVING, because the composite's saturation stage — documented at length in postfx.ts
 * since the previous pass and never actually wired into the shader — now runs. That was very
 * probably the largest single cause of the "muted" note this file kept being retuned against.
 *
 * Against architecture at wall 85-95.5, deck 53-69, soffit 24-34. Three relationships in that
 * list are load-bearing:
 *
 *  - SUNLIT WHITE (95.9) CLEARS THE BRIGHTEST SKY (91.2) BY 4.7 POINTS. That brightest sky is a
 *    single spot — the horizon directly under the sun, where the warm haze band, the horizon glow
 *    and the sun's wide halo lobe all land on top of each other — and everywhere else the margin
 *    is 13 to 34. It is the tightest relationship in the file and it is the reason HORIZON_GLOW
 *    is a tenth of what a prettier sky would want.
 *  - SHADED WHITE (85.6) CLEARS THE SKY ABOVE ABOUT FOUR DEGREES. Below that the two cross,
 *    which is unavoidable — a haze band has to be pale — and is confined to the strip the city
 *    skyline occupies anyway.
 *  - THE HORIZON LINE IS STILL SEVENTEEN POINTS (82.9 above, 65.5 below) and cannot be missed
 *    from any angle, which is how a player knows how high up they are.
 *
 * The sky now DOES reach into the deck band, but only at the top of the dome and only on chroma
 * the deck band cannot match; see ZENITH_DEEP, which sets out why that is safe and what would
 * make it unsafe. The sun disc remains the only thing in the game allowed to clip.
 */
const SKY_GAIN = 1.15;
/**
 * How far the zenith is pulled toward the horizon colour before the gain is applied.
 *
 * Was 0.68 — i.e. the zenith was two thirds of the way to being the horizon, because it had to
 * out-value a white wall. At 0.06 PALETTE.skyHigh survives essentially intact and the dome has
 * a real top: L* 67 at 62% chroma, deep, saturated, and unmistakably above you. That is twenty
 * points of vertical gradient with the chroma climbing the whole way up it, which is what a sky
 * actually does and what a single flat blue never looks like.
 */
const ZENITH_BLEND = 0.06;

/**
 * Pale hot haze right at the horizon — aerial perspective, and what the scene fog matches.
 *
 * PALE BLUE, NOT WHITE. The old value was two codes off pure white in green and blue and
 * rendered at 6% chroma, which is what made the bottom of the sky read as fog over the city
 * rather than as air in front of it. This still lands at L* 86 — the brightest part of the dome
 * by seven points, so the horizon still glows — but it keeps 22% of its chroma doing it.
 */
const HAZE = 0xc9e4fc;
/**
 * ...AND THE SUNWARD HALF OF THAT BAND IS WARM.
 *
 * Air scatters long wavelengths forward, so the sky within sixty degrees of the sun's azimuth is
 * genuinely creamier than the sky behind you. Every painter of a sunny morning knows it and it
 * is the cheapest possible "beautiful day" cue: warm light against cool shade is the whole
 * colour story of this art direction (DESIGN.md 4), and this is that story applied to the
 * biggest object in the frame.
 *
 * Chosen to sit within 0.3 L* of {@link HAZE} as measured through the grade with the sky's tint
 * exemption, so the band changes HUE around the horizon and not VALUE. A value seam sweeping
 * across the horizon as a player turns would read as a rendering bug; a hue arc reads as the sun
 * being over there.
 *
 * The value match is also what protects the one readability case this whole file has to get
 * right: a SUNLIT WHITE PARAPET (L* 95.9) SILHOUETTED AGAINST THE HORIZON DIRECTLY UNDER THE SUN.
 * That is the brightest sky the game can produce — haze plus the full horizon glow plus the sun's
 * wide halo lobe — and it measures L* 91.2, so 4.7 points of separation. An earlier draft of this
 * constant was brighter still and closed that to 4.2, which is where a white edge starts to go.
 * It was retuned rather than merely kept when the key went warmer (lighting.ts): this is the
 * warmest, palest thing in the dome and it has to travel with the sun that lights the world.
 */
const HAZE_WARM = 0xf9dcb8;
/**
 * The very top of the dome, above `skyHigh`. Deep, and only ever reached by actually looking up.
 *
 * A two-stop gradient cannot be a sky: it runs out of colour exactly where a real dome gets
 * deepest. This third stop is what makes looking up worth doing — L* 62.1 at 68% chroma, five
 * points under `skyHigh` and nineteen under the sky at head height. It fades in from 24 degrees
 * of elevation and only reaches full strength past 60, i.e. it costs nothing in the horizontal
 * gunfight and pays off entirely during traversal, when a player is airborne and looking up at
 * a grapple point. That is precisely the moment this game wants to feel good.
 *
 * IT IS INSIDE THE DECK BAND (54-69) AND THAT IS FINE — but only because of how it gets there,
 * so it is worth writing down rather than discovering again later. The old rule was "no part of
 * the sky lands inside the deck band, so a roof edge never dissolves", and the previous pass had
 * already crossed it (zenith 66.7). The rule was really a proxy for "the sky must never be
 * confusable with a surface", and up here two things enforce that far harder than 15 L* would:
 *
 *  - GEOMETRY. Deck band = up-facing surfaces. To see one against the zenith you would have to
 *    be underneath it, at which point you are looking at its soffit (L* 24-35), not its top.
 *    The pairing does not occur.
 *  - CHROMA. A sunlit deck measures b-r = -9 in sRGB codes; this measures b-r = +154. A hundred
 *    and sixty codes of hue is not a near-miss, and the ink outline draws the silhouette anyway
 *    at constant screen weight (DESIGN.md 4).
 *
 * The relationship that IS load-bearing near the horizon — sunlit white 95 over the brightest
 * sky 89.4 — is untouched by this stop, because this stop does not exist down there.
 */
const ZENITH_DEEP = 0x449ce6;
/**
 * Below the horizon: hazed, desaturated, and a hard step DOWN from the sky. That step is the
 * horizon line, and on a rooftop map the horizon line is the single most important thing in
 * the frame after the sun — it is what tells a player they are a hundred metres up.
 *
 * Lifted with the gain drop so the step stays where it was: twenty-one L* points below the haze
 * band immediately above it, and eleven above the arena's own shaded decks so a roof edge seen
 * against the drop never dissolves. There is no way to miss it.
 */
const GROUND_HAZE = 0x8aa3b8;
/**
 * Cloud body in sunlight — and it is WARM now, which is the point.
 *
 * It used to be a cool blue-white (0xdeebf7), i.e. the same hue as the sky, one value up. That is
 * what a cloud looks like on an overcast day, and it is why these read as pale shapes rather than
 * as objects with a sun on them. A sunlit cumulus top is lit by the same warm key that lights the
 * concrete (lighting.ts, KEY_COLOR) and it should say so: 0xefe7d8 renders #E9E3D4, which is 9
 * sRGB codes of red-over-blue where the old one was 19 codes of blue-over-red. Twenty-eight codes
 * of hue moved, and against an underside that went the other way (see CLOUD_SHADE) the pair now
 * carries the same warm-light/cool-shadow story as everything else in the frame.
 *
 * VALUE IS UNCHANGED AND HAD TO BE: L* 90.2, eleven points above the sky it sits on so a cloud
 * has real presence, and 5.7 BELOW sunlit white concrete (95.9) so a mast, a parapet or a player
 * crossing a cloud keeps an edge. Pure white was the obvious choice and the wrong one — it put
 * the softest, most mobile shape in the game at the same value as the hardest, most important
 * one.
 */
const CLOUD_LIT = 0xefe7d8;
/**
 * Cloud underside — cool blue, lifted, and far enough below the lit side to give a cloud a
 * readable form. L* 74.4 against a lit side of 90.2, i.e. sixteen points of form on a shape that
 * is only ever seen against a 62-85 sky.
 *
 * Pushed further into blue (0xa4bfdd -> 0x9dbcdf, 26% -> 32% chroma) alongside the lit tone going
 * warm. A cumulus base is not grey; it is the sky's own light and nothing else, exactly like a
 * shadow on a deck, and it should be the same colour as one.
 */
const CLOUD_SHADE = 0x9dbcdf;
/** Thin high cirrus. Pale, very low opacity; this layer is texture, not shape. */
const CIRRUS_COLOR = 0xe0edfa;
/** The city's own tone before haze. Cool slate: distant concrete under a blue sky. */
const CITY_COLOR = 0x8ba1b9;

/**
 * cos of the sun disc's angular radius — about 2 degrees, so a 4-degree disc. Four times the
 * real sun, which is the right amount of stylisation, and deliberately small: it is the hottest
 * thing in the game, and a hot object big enough to hide a player standing in front of it is a
 * readability bug (DESIGN.md 7), not a sunset.
 */
const SUN_COS = 0.99935;
/**
 * Antialiasing width of the disc's edge, in cos units. d(cos)/d(pixel) at this radius and the
 * game's FOV is around 3.6e-5, so this is roughly three pixels of softness — the disc reads as
 * a clean-edged shape, which is what a cel sky wants.
 */
const SUN_EDGE = 0.00005;
/**
 * How hot the disc itself burns, as a multiple of the sun colour. The one thing allowed to clip.
 *
 * Raised in step with the SKY_GAIN drop (3.6 x 1.35 = 4.86 of sun radiance, 4.2 x 1.15 = 4.83)
 * so the dome coming down does not take the sun down with it. The disc has to stay the hottest
 * object in the game — it is what the shafts, the halo and the HIGH NOON glare all originate
 * from, and against a deeper sky it now has more to burn against, not less.
 */
const SUN_DISC_GAIN = 4.2;
/**
 * Warm glow along the horizon, strongest toward the sun's azimuth. Restrained on purpose: a
 * wide bright wash on the horizon is exactly where an enemy silhouette would be lost, so this
 * is a tint on an already-pale band rather than a second light source.
 *
 * DOWN FROM 0.10 because the haze band it sits on is warm now (see HAZE_WARM), so it is no
 * longer the only thing saying "the sun is over there" and no longer has to shout. The pair
 * together put the hottest pixel of horizon at L* 89.6, against 88.9 for the old cool band plus
 * the louder glow — i.e. slightly more warmth for slightly less brightness, which is the trade
 * this whole pass is made of.
 */
const HORIZON_GLOW = 0.085;

/** fbm threshold for cloud body. fbm sits around 0.47, so this keeps coverage sparse. */
const CLOUD_COVER = 0.53;
const CLOUD_OPACITY = 0.95;
/** Edge softness of a cloud, in fbm units. Soft, not stepped: these are painted, not inked. */
const CLOUD_SOFT = 0.05;

/**
 * THE TWO CLOUD DECKS, in metres: how high the virtual plane sits, and how wide one noise cell
 * is on it.
 *
 * Apparent size in the sky is PLANE / FEATURE and nothing else, so the near deck at 440/293 and
 * the far deck at 2200/957 give 1.50 and 2.30 cells per unit of `dir.xz / dir.y` — i.e. the far
 * deck's clouds are half again smaller on screen, which is exactly what "further away" looks
 * like without any extra machinery.
 *
 * PARALLAX IS WHY THE ABSOLUTE HEIGHTS MATTER AT ALL. The sample point is offset by the camera's
 * world position over FEATURE, so a deck slides by (metres travelled / FEATURE) cells. Over the
 * ~110m width of the arena that is 0.38 cells for the near deck and 0.11 for the far one, and
 * the DIFFERENCE between those two — a quarter of a cloud-width of relative slide — is the depth
 * cue. Both planes are lower than real cumulus for exactly this reason: at a realistic 2km the
 * near deck would slide 0.06 cells across the whole map and the sky would be a painted box.
 *
 * THE NEAR DECK CAME DOWN AGAIN (520 -> 440m) WITH ITS FEATURE SIZE IN PROPORTION, so it looks
 * identical and slides 18% further. That ratio is the only free variable here: apparent size is
 * fixed by PLANE / FEATURE and parallax is fixed by 1 / FEATURE, so bringing the whole deck
 * closer buys depth at no cost to the composition. It stops where it does because below about
 * 400m the near deck starts sliding fast enough during a grapple swing to read as a moving
 * object rather than as distance.
 *
 * Camera rotation cannot produce any of this, which is why the previous version had none.
 */
const CLOUD_PLANE_NEAR = 440.0;
const CLOUD_FEATURE_NEAR = 293.0;
const CLOUD_PLANE_FAR = 2200.0;
const CLOUD_FEATURE_FAR = 957.0;
/**
 * How far toward the sun the second density tap is taken, in cells. This is the whole lighting
 * model for the near deck: sample the field again 0.30 of a cloud-width sunward, and if it came
 * back thinner then light reaches this point.
 *
 * Small enough to stay inside one cloud (so it shades the cloud's own form rather than comparing
 * two different clouds), large enough that the difference clears the noise floor of a
 * four-octave field.
 *
 * WHICH WAY IS "SUNWARD" IS THE PART THAT CHANGED, and it is what buys lit tops and cool
 * undersides rather than one flank lit on every cloud in the sky. The tap used to be taken along
 * a CONSTANT direction — the sun's azimuth, flattened — which is the direction light travels
 * across the deck only for a cloud infinitely far away. On the actual projected plane the sun has
 * a POSITION: it pierces the deck at `sunFlat / sin(elevation)`, i.e. 1.48 cp-units out along its
 * own azimuth at the 34-degree key angle, and light spreads outward from that point.
 *
 * Sampling toward that point instead of along a fixed vector costs one normalize and changes the
 * whole read of the sky:
 *
 *  - A cloud out near the horizon is far from the sun's piercing point, so its "toward the sun"
 *    direction points back up the dome — toward the zenith, which is UP ON SCREEN. Its top lights
 *    and its underside stays cool. That is the note this pass exists to answer.
 *  - A cloud overhead sits close to the piercing point, so it lights on the side facing the sun's
 *    screen position, with the far side going blue. Correct, and it is the same cloud shading
 *    differently depending on where it is, which is the thing a constant direction can never do.
 *  - Clouds sitting directly between the camera and the sun light from behind, so the silver
 *    lining (CLOUD_RIM) fires on the rim facing you. Free, and it is the prettiest frame the sky
 *    can produce.
 */
const CLOUD_SUN_STEP = 0.3;
/**
 * Floor under the sun's elevation when projecting it onto a cloud plane. `sunFlat / dir.y` is a
 * division by the sine of the elevation, which runs away as the sun approaches the horizon; the
 * sun never gets below 28 degrees (lighting.ts, HIGH NOON) so this clamp is never reached in
 * practice, and exists only so the expression cannot produce an infinity if it ever is.
 */
const CLOUD_SUN_ELEV_MIN = 0.12;
/**
 * AERIAL PERSPECTIVE ON THE CLOUD DECK. How far a cloud at the horizon is mixed toward the sky
 * immediately behind it, and the elevation over which that fades out.
 *
 * The far skyline has had aerial perspective since the beginning and the clouds had none, so the
 * deck read as a flat sheet of decals laid over a dome that was itself receding. This is the same
 * cue applied to the same distances: a cloud low in the sky is a long way off, so it loses
 * contrast and takes on the hue of the air in front of it — which near the sun's azimuth is the
 * warm haze band, so distant clouds go cream in the direction of the sun and stay cool behind
 * you. That is a morning sky, and it costs one mix against a colour the shader already has.
 */
const CLOUD_DISTANCE_HAZE = 0.72;
const CLOUD_HAZE_TOP = 0.34;
/** Density difference over which a flank goes from fully shaded to fully lit. */
const CLOUD_SHADE_LO = -0.055;
const CLOUD_SHADE_HI = 0.075;
/**
 * SILVER LINING. A thin hot rim of sun colour on the sunward EDGE of a cloud — the outer band of
 * the body mask, gated on the self-shadow term so it only appears where the sun is actually
 * getting in. This is the single prettiest thing in a sunny sky and it is nearly free; it also
 * carries a little bloom (see the mask at the bottom of the shader), which is the only place in
 * this file other than the sun itself that is allowed to.
 */
const CLOUD_RIM = 0.42;
const CLOUD_RIM_BLOOM = 0.42;
/**
 * How much a cloud directly overhead is pushed toward its shaded tone. You see the flat base of
 * a cloud that is above you and the sunlit flank of one out toward the horizon, so this is real
 * — but it is kept mild, because a dark ceiling of cloud is the opposite of the brief.
 */
const CLOUD_OVERHEAD_SHADE = 0.4;
/** Far deck: sparser, softer, and half the opacity. It is depth, not subject matter. */
const CLOUD_FAR_CUT = 0.03;
const CLOUD_FAR_OPACITY = 0.55;
/**
 * THE SHAPE OF THE DOME, in units of dir.y (i.e. the sine of elevation).
 *
 * HAZE_TOP is where the pale horizon band has finished dissolving into the sky above it, and it
 * is HALVED from 0.16. That single number was the largest cause of "the sky looks like milk":
 * 0.16 is 9.2 degrees of elevation, and a rooftop FPS camera is pitched at or below the horizon
 * most of the time, so the palest, least saturated eighth of the dome was most of the visible
 * sky in most frames. At 0.085 it is a 4.9-degree strip that sits behind the city skyline rather
 * than behind the play space.
 *
 * ZENITH_H is where the mid sky has fully become `skyHigh`, down from 0.72 (46 deg) to 0.46
 * (27 deg), so the dome actually arrives at its colour inside the range a player looks at. The
 * SUN_BIAS term stretches that transition toward the sun's azimuth, because a real sky IS paler
 * around the sun and deepest opposite it — a free second axis of variation that makes turning
 * around feel like turning around.
 *
 * Measured down the gradient, through the grade with the sky's tint exemption:
 *
 *      3 deg  L* 87  chroma 19-21%   (haze band, warm sunward / cool away)
 *      6 deg  L* 79  chroma 43%
 *     12 deg  L* 75  chroma 47%
 *     20 deg  L* 69  chroma 56%
 *     30 deg  L* 66  chroma 62%
 *     60 deg  L* 64  chroma 66%
 *     90 deg  L* 62  chroma 68%
 *
 * The old dome was still at 79 / 35% at twelve degrees. Same top, same bottom, but the climb
 * now happens where the camera is pointed.
 */
const HAZE_TOP = 0.085;
const ZENITH_H = 0.46;
const ZENITH_SUN_BIAS = 0.16;
/** Where the deep zenith stop fades in and out. 24 deg to past vertical: see ZENITH_DEEP. */
const ZENITH_DEEP_LO = 0.42;
const ZENITH_DEEP_HI = 1.05;

/** High cirrus: finer, stretched along the drift axis, and barely there. */
const CIRRUS_SCALE = 1.35;
const CIRRUS_STRETCH = 5.5;
const CIRRUS_CUT = 0.55;
const CIRRUS_OPACITY = 0.3;

/**
 * Skyline bands, far to near. `freq` is buildings per radian of azimuth (higher = smaller, so
 * it reads as further away), `base`/`height` are roofline heights in units of dir.y, and
 * `haze` is how far the band is mixed into the sky behind it.
 *
 * AERIAL PERSPECTIVE IS THE ONLY DEPTH CUE at this distance, so the haze spread is the whole
 * effect: the far band is a 90%-dissolved tint you read as atmosphere rather than as
 * buildings, and the near band holds ~15 points of value below the sky so the roofline is
 * unmistakable. Nothing out there is ever allowed to become a hard dark silhouette, because a
 * hard dark shape on the horizon is indistinguishable from a player at range.
 */
const CITY_BANDS = [
  { freq: 31.0, seed: 11.0, base: 0.006, height: 0.05, haze: 0.82 },
  { freq: 17.0, seed: 43.0, base: 0.0, height: 0.085, haze: 0.58 },
  { freq: 9.0, seed: 79.0, base: -0.016, height: 0.125, haze: 0.3 },
] as const;

/** Below this elevation the city has sunk into the ground haze entirely. */
const CITY_SINK_LOW = -0.3;
const CITY_SINK_HIGH = -0.04;

const SKY_VERT = /* glsl */ `
varying vec2 vNdc;

void main() {

	vNdc = position.xy;
	// Straight to the far plane in clip space; depthFunc LEqual lets it fill only the
	// pixels nothing else claimed.
	gl_Position = vec4( position.xy, 1.0, 1.0 );

}
`;

/** One skyline band. Blocks with an occasional setback crown, antialiased against fwidth. */
const CITY_GLSL = /* glsl */ `
float cityBand( float x, float h, float base, float height, float seed ) {

	float i = floor( x );
	float f = fract( x );

	float r = hash11( i * 1.13 + seed );
	float r2 = hash11( i * 2.71 + seed + 7.0 );

	// Width of the block inside its cell; the remainder is sky. r*r skews the roofline low,
	// so most of the city is flat and a handful of towers stand out of it.
	float w = 0.52 + 0.40 * r2;
	float top = base + height * r * r;

	float isTower = step( 0.76, r );
	float crownW = w * ( 0.28 + 0.26 * r2 );
	float crownTop = mix( top, top + height * 0.5, isTower );

	// fwidth on x rather than on fract(x): fract has a discontinuity at every cell edge and
	// its derivative blows up there, which would smear one column per building into mush.
	float aax = clamp( fwidth( x ), 1e-5, 0.25 ) * 1.1;
	float aay = clamp( fwidth( h ), 1e-6, 0.25 ) * 1.1;

	float body = ( 1.0 - smoothstep( w - aax, w + aax, f ) )
		* ( 1.0 - smoothstep( top - aay, top + aay, h ) );
	float crown = ( 1.0 - smoothstep( crownW - aax, crownW + aax, f ) )
		* ( 1.0 - smoothstep( crownTop - aay, crownTop + aay, h ) );

	return clamp( max( body, crown ), 0.0, 1.0 );

}
`;

const SKY_FRAG = /* glsl */ `
precision highp float;

varying vec2 vNdc;

uniform mat4 uInvProjection;
uniform mat3 uCameraRot;
uniform vec3 uSunDir;
uniform float uTime;
/** World position of the camera. Drives the cloud decks' parallax and nothing else. */
uniform vec3 uCameraPos;

uniform vec3 uSky;
uniform vec3 uSkyHigh;
uniform vec3 uZenithDeep;
uniform vec3 uHaze;
uniform vec3 uHazeWarm;
uniform vec3 uGroundHaze;
uniform vec3 uCloudLit;
uniform vec3 uCloudShade;
uniform vec3 uCirrus;
uniform vec3 uSunColor;
uniform vec3 uCityColor;
uniform float uCloudCut;

float hash11( float p ) {

	p = fract( p * 0.1031 );
	p *= p + 33.33;
	p *= p + p;
	return fract( p );

}

float hash21( vec2 p ) {

	vec3 q = fract( vec3( p.xyx ) * 0.1031 );
	q += dot( q, q.yzx + 33.33 );
	return fract( ( q.x + q.y ) * q.z );

}

float vnoise( vec2 p ) {

	vec2 i = floor( p );
	vec2 f = fract( p );
	vec2 u = f * f * ( 3.0 - 2.0 * f );
	float a = hash21( i );
	float b = hash21( i + vec2( 1.0, 0.0 ) );
	float c = hash21( i + vec2( 0.0, 1.0 ) );
	float d = hash21( i + vec2( 1.0, 1.0 ) );
	return mix( mix( a, b, u.x ), mix( c, d, u.x ), u.y );

}

float fbm( vec2 p ) {

	float s = 0.0;
	float a = 0.5;
	for ( int i = 0; i < 4; i ++ ) {
		s += a * vnoise( p );
		p = p * 2.07 + 13.1;
		a *= 0.5;
	}
	return s;

}

/** Two octaves. The cirrus and the far cloud deck are texture, not shape, so two is enough. */
float fbm2( vec2 p ) {

	return 0.5 * vnoise( p ) + 0.25 * vnoise( p * 2.07 + 13.1 );

}

/**
 * Three octaves, for the domain warp only. The warp decides where a cloud BENDS; the detail it
 * would gain from a fourth octave is detail the field it warps already has, so paying for it
 * twice is the one saving available here — and this shader now takes three fbm taps per pixel
 * for the near deck instead of two, so the saving is spent rather than pocketed.
 */
float fbm3( vec2 p ) {

	float s = 0.0;
	float a = 0.5;
	for ( int i = 0; i < 3; i ++ ) {
		s += a * vnoise( p );
		p = p * 2.07 + 13.1;
		a *= 0.5;
	}
	return s;

}

${CITY_GLSL}

void main() {

	// Reconstruct the world-space view ray from NDC.
	vec4 nearPoint = uInvProjection * vec4( vNdc, - 1.0, 1.0 );
	vec3 viewDir = normalize( nearPoint.xyz / nearPoint.w );
	vec3 dir = normalize( uCameraRot * viewDir );

	float h = dir.y;

	// Which way is the sun, flattened to the ground plane. Used by the horizon glow, the city's
	// sunward faces and the clouds' lit side, so all three agree about where the light is.
	vec2 sunFlat = normalize( vec2( uSunDir.x, uSunDir.z ) );
	vec2 dirFlat = normalize( vec2( dir.x, dir.z ) + 1e-5 );
	float sunSide = 0.5 + 0.5 * dot( sunFlat, dirFlat );

	// --- THE DOME. haze -> sky -> zenith, with the ground haze stepping down below the
	//     horizon line. Smooth on purpose; the cel banding in this world lives on surfaces,
	//     never in the sky (DESIGN.md 4).
	//
	//     POSTERISATION IS A REAL RISK HERE and it is handled downstream, not by flattening the
	//     gradient. Twenty-one L* points spread over a screen height is about 0.026 L* per
	//     pixel, while one 8-bit code up at L* 80 is worth 0.35 — i.e. a naive encode would
	//     stripe the sky with a visible band every thirteen pixels. The composite's triangular
	//     sub-LSB dither (postfx.ts, at the sRGB encode) is what breaks those steps into noise
	//     below the visible threshold, and FXAA leaves it alone because a smooth gradient never
	//     trips its contrast test. This is the reason that dither exists.
	//
	//     THREE STOPS, NOT TWO, and the transitions are placed against where a first-person
	//     camera actually points rather than against the dome's own geometry. See HAZE_TOP.
	//
	//     The haze band is warm toward the sun and cool away from it. pow(sunSide, 1.6) keeps
	//     the cream inside roughly a sixty-degree arc instead of smearing it round the whole
	//     horizon, which is the difference between morning and smog.
	vec3 haze = mix( uHaze, uHazeWarm, pow( sunSide, 1.6 ) );

	// The mid->zenith transition is stretched toward the sun, because a real sky is palest
	// around the sun and deepest opposite it. One extra term, one more reason to look around.
	float zenithK = smoothstep( 0.01, ${ZENITH_H.toFixed(3)} + ${ZENITH_SUN_BIAS.toFixed(3)} * sunSide, h );
	vec3 col = mix( uSky, uSkyHigh, zenithK );
	col = mix( col, uZenithDeep, smoothstep( ${ZENITH_DEEP_LO.toFixed(3)}, ${ZENITH_DEEP_HI.toFixed(3)}, h ) );
	col = mix( haze, col, smoothstep( 0.0, ${HAZE_TOP.toFixed(3)}, h ) );
	col = mix( uGroundHaze, col, smoothstep( - 0.06, 0.005, h ) );

	// --- WARM HORIZON GLOW. Confined to the bottom sixth of the dome and biased hard toward
	//     the sun's azimuth, so it reads as atmosphere near the sun rather than as a global
	//     brightening of the one band a player silhouette would appear against.
	float horizon = 1.0 - smoothstep( 0.0, 0.17, max( h, 0.0 ) );
	col += uSunColor * ( horizon * horizon * pow( sunSide, 2.5 ) * ${HORIZON_GLOW.toFixed(3)} );

	// --- warm sun halo. Warms the sky around the disc, which sells "the sun is over there"
	//     without a lens-flare gimmick. Two lobes: a tight hot one and a wide faint one.
	float sd = dot( dir, uSunDir );
	float sdc = max( sd, 0.0 );
	col += uSunColor * ( pow( sdc, 14.0 ) * 0.125 + pow( sdc, 3.2 ) * 0.042 );

	// --- the city. Sunward faces catch a little warmth; every band is mixed toward the sky
	//     by its own haze amount, and the whole thing sinks into the ground haze below the
	//     horizon. Drawn near-to-far order by simply compositing far first.
	float azimuth = atan( dir.z, dir.x );
	float citySink = smoothstep( ${CITY_SINK_LOW.toFixed(3)}, ${CITY_SINK_HIGH.toFixed(3)}, h );
	vec3 cityLit = mix( uCityColor, uCityColor * 1.22 + uSunColor * 0.06, sunSide );
`;

const SKY_FRAG_TAIL = /* glsl */ `

	// --- CLOUDS, on two virtual planes at different altitudes. max() on the denominator keeps
	//     derivatives defined everywhere (no branching), which is what stops the edges sparkling
	//     near the horizon.
	//
	//     dir.xz / up * PLANE is where this view ray pierces the plane, in metres. Subtracting
	//     the camera's own world position is the entire parallax implementation: move, and the
	//     near deck slides against the far one. Dividing by FEATURE puts it in noise cells.
	float up = max( h, 0.03 );
	vec2 cp = dir.xz / up;
	vec2 drift = vec2( uTime * 0.0105, uTime * 0.0038 );

	vec2 pNear = ( cp * ${CLOUD_PLANE_NEAR.toFixed(1)} - uCameraPos.xz ) * ${(1 / CLOUD_FEATURE_NEAR).toFixed(8)} + drift;

	// Domain-warped fbm. The warp is what stops them reading as noise blobs — it gives a cloud
	// a direction — and it only needs three octaves because the field it warps supplies the rest.
	// The amplitude is up from 0.68/0.36: a stronger warp bends a cell into a lobed, shouldered
	// silhouette instead of a rounded lump, which is the difference between "cumulus" and "blob".
	float warp = fbm3( pNear * 0.62 );
	vec2 wNear = pNear + vec2( warp * 0.92, warp * 0.48 );
	float n = fbm( wNear );

	// THE LIGHTING. One extra tap of the same field, taken toward the sun. If the cloud is
	// thinner over there, light is reaching this point; if it is thicker, this point is behind
	// the rest of the cloud. That is a one-sample self-shadow, and it is the whole reason these
	// have form instead of being flat blobs with a soft edge — an older version shaded by
	// distance-to-edge, which lights every cloud identically from every direction at once.
	//
	// TOWARD THE SUN'S PROJECTED POSITION ON THIS PLANE, not along a constant azimuth: that is
	// what makes a cloud near the horizon light on its TOP and one overhead light on its flank.
	// See CLOUD_SUN_STEP for the full argument.
	vec2 sunCp = sunFlat / max( uSunDir.y, ${CLOUD_SUN_ELEV_MIN.toFixed(3)} );
	vec2 toSun = normalize( sunCp - cp + 1e-4 );
	float nSun = fbm( wNear + toSun * ${CLOUD_SUN_STEP.toFixed(3)} );
	float sunward = n - nSun;

	float cover = smoothstep( 0.012, 0.19, h );
	float d = n - uCloudCut;
	float body = smoothstep( 0.0, ${CLOUD_SOFT.toFixed(3)}, d );

	// You see the flat base of a cloud that is above you and the sunlit flank of one out toward
	// the horizon. Mild: a dark ceiling of cloud is the opposite of the brief.
	float overhead = smoothstep( 0.30, 0.90, h );
	float lit = smoothstep( ${CLOUD_SHADE_LO.toFixed(3)}, ${CLOUD_SHADE_HI.toFixed(3)}, sunward );
	// The trailing term is a floor on how dark a cloud on the sunny side of the sky may get. It
	// used to be 0.16 and is 0.10 now: it was standing in for the directionality the self-shadow
	// could not supply while the tap direction was a constant, and the projected-sun tap above
	// supplies it properly. Every point of it that survives is a point of form washed flat.
	lit = clamp( lit * ( 1.0 - ${CLOUD_OVERHEAD_SHADE.toFixed(2)} * overhead ) + 0.10 * sunSide, 0.0, 1.0 );

	vec3 cloud = mix( uCloudShade, uCloudLit, lit );

	// SILVER LINING: the outer band of the body mask, but only where the sun is getting in.
	float rim = body * ( 1.0 - smoothstep( ${(CLOUD_SOFT * 1.1).toFixed(3)}, ${(CLOUD_SOFT * 3.2).toFixed(3)}, d ) )
		* smoothstep( 0.0, 0.06, sunward );
	cloud += uSunColor * ( rim * ${CLOUD_RIM.toFixed(3)} );

	// AERIAL PERSPECTIVE, on the clouds as well as on the city. col here is the dome and the
	// skyline, i.e. exactly the air standing between the camera and this cloud, so mixing toward
	// it is the correct operator and not an approximation of one. Low clouds lose their form and
	// take the hue of the haze band — cream toward the sun, cool away from it — which is what
	// puts distance into a deck that used to read as one flat sheet.
	float cloudFade = ${CLOUD_DISTANCE_HAZE.toFixed(3)} * ( 1.0 - smoothstep( 0.02, ${CLOUD_HAZE_TOP.toFixed(3)}, h ) );
	cloud = mix( cloud, col, cloudFade );

	float cloudA = body * ${CLOUD_OPACITY.toFixed(2)} * cover;

	// THE FAR DECK: higher plane, smaller features, sparser, half the opacity, and lit by azimuth
	// alone. Two octaves — at this apparent size a self-shadow would be sub-pixel detail, and its
	// entire job is to give the near deck something to slide against.
	vec2 pFar = ( cp * ${CLOUD_PLANE_FAR.toFixed(1)} - uCameraPos.xz ) * ${(1 / CLOUD_FEATURE_FAR).toFixed(8)} + drift * 0.55 + 41.7;
	// fbm2's weights sum to 0.75 against fbm's 0.9375, so rescale before reusing the same cut.
	float nFar = fbm2( pFar ) * 1.25;
	float dFar = nFar - ( uCloudCut + ${CLOUD_FAR_CUT.toFixed(3)} );
	vec3 cloudFar = mix( uCloudShade, uCloudLit, clamp( 0.35 + 0.65 * sunSide, 0.0, 1.0 ) );
	cloudFar = mix( cloudFar, col, cloudFade );
	float cloudFarA = smoothstep( 0.0, ${(CLOUD_SOFT * 1.6).toFixed(3)}, dFar )
		* ${CLOUD_FAR_OPACITY.toFixed(2)} * smoothstep( 0.02, 0.16, h );

	// Cirrus: the same field at a finer scale, squashed along one axis so it streaks. Drawn
	// UNDER the cumulus (i.e. first) and at a third of the opacity — it is there to keep a big
	// empty patch of zenith from being a flat wash, and for nothing else.
	//
	// It fades in a long way above the horizon on purpose. dir.xz / dir.y blows up as dir.y
	// approaches zero, so a fine-frequency layer sampled down there would alias into sparkle no
	// matter how it was filtered; the cumulus layer gets away with the same construction because
	// four octaves of value noise are far smoother than two.
	vec2 hp = ( cp * ${CIRRUS_SCALE.toFixed(2)} + drift * 2.4 ) * vec2( 1.0 / ${CIRRUS_STRETCH.toFixed(2)}, 1.0 );
	float cir = smoothstep( ${CIRRUS_CUT.toFixed(3)}, ${CIRRUS_CUT.toFixed(3)} + 0.13, fbm2( hp ) );
	col = mix( col, uCirrus, cir * ${CIRRUS_OPACITY.toFixed(2)} * smoothstep( 0.12, 0.45, h ) );

	// Far deck first, then near over it. Compositing order IS the depth order.
	col = mix( col, cloudFar, cloudFarA );
	col = mix( col, cloud, cloudA );

	// --- the sun disc itself, last, so nothing paints over it.
	float disc = smoothstep( ${SUN_COS.toFixed(5)} - ${SUN_EDGE.toFixed(6)}, ${SUN_COS.toFixed(5)} + ${SUN_EDGE.toFixed(6)}, sd );
	col = mix( col, uSunColor * ${SUN_DISC_GAIN.toFixed(2)}, disc );

	// Alpha is the selective-bloom mask ( 1 - bloom ). The disc blooms hard and the hot inner
	// halo blooms a little, so the sun reads as a light source rather than as a white circle.
	// The prefilter gates the mask at 0.3..0.6 to reject alpha-blended geometry that never asked
	// to glow, so the halo term has to clear 0.3 to exist at all — hence 0.6 rather than a token
	// amount. pow(.,160) is about a four-degree skirt around a four-degree disc.
	//
	// THE SILVER LINING IS THE ONLY OTHER THING IN THE SKY THAT BLOOMS, and it is a deliberate
	// reading of DESIGN.md 4's "bloom on the sun and on emissives only": a lit cloud edge is the
	// sun, seen indirectly. It is confined to a rim a few pixels wide, it never covers area, and
	// at 0.42 it lands just past the prefilter's gate — about a third of full strength. What is
	// still ruled out, explicitly, is the whole dome glowing: the body of a cloud, the haze band
	// and the horizon all write a mask of exactly zero.
	float haloBloom = pow( sdc, 160.0 ) * 0.6;
	// The silver lining's share of the mask is faded by the same aerial perspective that fades
	// the lining itself, or a cloud hazed to invisibility on the horizon would still be pushing
	// a bloom source into the pyramid.
	float bloomMask = clamp(
		disc * 0.9 + haloBloom + rim * ${CLOUD_RIM_BLOOM.toFixed(3)} * cloudA * ( 1.0 - cloudFade ),
		0.0,
		1.0
	);
	gl_FragColor = vec4( col, 1.0 - bloomMask );

}
`;

/** Emit one composite step per skyline band. Unrolled so the loop body can be constant-folded. */
function cityBandsGlsl(): string {
  let src = '';
  for (const b of CITY_BANDS) {
    src += /* glsl */ `
	{
		float m = cityBand( azimuth * ${b.freq.toFixed(2)} + ${b.seed.toFixed(1)}, h, ${b.base.toFixed(4)}, ${b.height.toFixed(4)}, ${b.seed.toFixed(1)} );
		vec3 tone = mix( cityLit, col, ${b.haze.toFixed(3)} );
		col = mix( col, tone, m * citySink );
	}
`;
  }
  return src;
}

export interface Sky {
  readonly object: THREE.Mesh;
  /**
   * Colour to tie scene fog to, so distant BACKDROP geometry melts into the horizon haze.
   *
   * Deliberately NOT gained by SKY_GAIN. Fog is a lerp toward this colour applied to every
   * surface in the scene, so a fog colour at full sky brightness would drag anything at range
   * up toward white. The renderer keeps FOG_NEAR out past the arena's own sightlines so this
   * only ever touches the city; the sky sells aerial perspective inside its own shader, where
   * it costs the architecture nothing.
   *
   * It is also a fog colour rather than a sky colour on purpose, and the ungained figure is
   * what makes that work out: a fully-hazed distant tower renders at L* 83.7 against a horizon
   * sky of 86.3, so the far city converges on the sky from BELOW and never becomes brighter
   * than the air in front of it. Aerial perspective that overshoots reads as a light source at
   * the horizon, which is the one thing a city at range must never look like.
   */
  readonly horizonColor: THREE.Color;
  setSunDirection(dir: THREE.Vector3): void;
  setPixelRatio(v: number): void;
  update(camera: THREE.PerspectiveCamera, elapsedSec: number): void;
  dispose(): void;
}

const _camRot = new THREE.Matrix3();

/**
 * Palette colour -> linear scene radiance, with the sky's exposure gain baked in.
 *
 * Applying the gain here rather than at the end of the fragment shader matters: every mix in
 * that shader (haze into sky, city into haze, cloud into sky) then happens between values that
 * are already at final scale, so a hazed building really does converge on the sky it is being
 * hazed into instead of on a dimmer copy of it.
 */
function skyColor(hex: number, blendToHorizon = 0): THREE.Color {
  const c = new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
  if (blendToHorizon > 0) {
    c.lerp(new THREE.Color().setHex(PALETTE.sky, THREE.SRGBColorSpace), blendToHorizon);
  }
  return c.multiplyScalar(SKY_GAIN);
}

export function createSky(): Sky {
  const uniforms: Record<string, THREE.IUniform> = {
    uInvProjection: { value: new THREE.Matrix4() },
    uCameraRot: { value: new THREE.Matrix3() },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uTime: { value: 0 },
    uCameraPos: { value: new THREE.Vector3() },
    uSky: { value: skyColor(PALETTE.sky) },
    uSkyHigh: { value: skyColor(PALETTE.skyHigh, ZENITH_BLEND) },
    uZenithDeep: { value: skyColor(ZENITH_DEEP) },
    uHaze: { value: skyColor(HAZE) },
    uHazeWarm: { value: skyColor(HAZE_WARM) },
    uGroundHaze: { value: skyColor(GROUND_HAZE) },
    uCloudLit: { value: skyColor(CLOUD_LIT) },
    uCloudShade: { value: skyColor(CLOUD_SHADE) },
    uCirrus: { value: skyColor(CIRRUS_COLOR) },
    uSunColor: { value: skyColor(PALETTE.sun) },
    uCityColor: { value: skyColor(CITY_COLOR) },
    uCloudCut: { value: CLOUD_COVER },
  };

  const material = new THREE.ShaderMaterial({
    name: 'OneTapSky',
    uniforms,
    vertexShader: SKY_VERT,
    fragmentShader: SKY_FRAG + cityBandsGlsl() + SKY_FRAG_TAIL,
    side: THREE.DoubleSide,
    depthTest: true,
    depthWrite: false,
    depthFunc: THREE.LessEqualDepth,
    transparent: false,
    fog: false,
    toneMapped: false,
  });

  const geometry = new THREE.PlaneGeometry(2, 2);
  const object = new THREE.Mesh(geometry, material);
  object.name = 'Sky';
  object.frustumCulled = false;
  // Draw after the arena so the depth test rejects every covered pixel.
  object.renderOrder = 1000;
  object.matrixAutoUpdate = false;

  const horizonColor = new THREE.Color().setHex(HAZE, THREE.SRGBColorSpace);

  return {
    object,
    horizonColor,
    setSunDirection(dir: THREE.Vector3): void {
      (uniforms.uSunDir!.value as THREE.Vector3).copy(dir).normalize();
    },
    setPixelRatio(_v: number): void {
      /* nothing screen-space left in the sky since the halftone was removed */
    },
    update(camera: THREE.PerspectiveCamera, elapsedSec: number): void {
      (uniforms.uInvProjection!.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
      _camRot.setFromMatrix4(camera.matrixWorld);
      (uniforms.uCameraRot!.value as THREE.Matrix3).copy(_camRot);
      // The cloud decks are sampled in WORLD metres on their virtual planes, so this is what
      // gives them parallax. Read off matrixWorld rather than `.position` because the renderer
      // offsets the camera for view-bob and recoil after setting position, and a sky that only
      // saw the un-bobbed position would slide against a view that was moving.
      (uniforms.uCameraPos!.value as THREE.Vector3).setFromMatrixPosition(camera.matrixWorld);
      uniforms.uTime!.value = elapsedSec;
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
    },
  };
}
